import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, InputAdornment, FormControl, InputLabel,
  Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Accordion, AccordionSummary,
  AccordionDetails, Grid, Tabs, Tab, Chip, Paper,
  Autocomplete,
} from '@mui/material';
import {
  Search, Tune, ExpandMore, QrCode2,
} from '@mui/icons-material';
import { useForm, useFieldArray, useWatch, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import DataTable from '../components/common/DataTable';
import { inventoryAPI, productsAPI } from '../services/api';

// ─── helper: condition colour ───────────────────────────────────
// ─── EMPTY UNIT ROW ─────────────────────────────────────────────
const emptyUnit = () => ({
  chassis_number: '', condition: 'new', color: '', year: '',
  notes: '', custom_attributes: {},
});

const COLOR_OPTIONS = [
  'Black', 'Blue', 'Brown', 'Gold', 'Gray', 'Green', 'Orange',
  'Red', 'Silver', 'White', 'Yellow', 'Beige', 'Maroon', 'Navy',
  'Pearl', 'Purple',
];

const YEAR_OPTIONS = Array.from(
  { length: new Date().getFullYear() + 2 - 1980 },
  (_, index) => String(new Date().getFullYear() + 1 - index),
);

// ══════════════════════════════════════════════════════════════
// ADJUST DIALOG  (bulk stock + optional per-unit rows)
// ══════════════════════════════════════════════════════════════
const AdjustDialog = ({ open, onClose, onSaved, warehouses }) => {
  const { register, handleSubmit, control, watch, reset, formState: { errors } } = useForm({
    defaultValues: { movement_type: 'in', quantity: 1, stock_alert: 0 },
  });
  const { fields, append, remove, replace } = useFieldArray({ control, name: 'units' });

  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [unitsExpanded, setUnitsExpanded] = useState(false);

  const movType = watch('movement_type');
  const qty = parseInt(watch('quantity')) || 0;
  const showUnits = (movType === 'in' || movType === 'return') && qty > 0;
  // Subscribe directly to the field-array values. `watch('units')` can retain
  // the same array reference while a registered input is being edited, which
  // leaves the submit button stale until another state change (such as
  // collapsing the accordion) causes a re-render.
  const unitsData = useWatch({ control, name: 'units', defaultValue: [] });

  // Validate that all required unit fields are filled when tracking units
  const areUnitsValid = useMemo(() => {
    // If not a unit-tracking movement type, always valid
    if (!showUnits) return true;
    
    // If accordion is not expanded but we have units that should be tracked, invalid
    if (!unitsExpanded && qty > 0) return false;
    
    if (unitsExpanded && unitsData?.length > 0) {
      const allValid = unitsData.every(unit => {
        const hasIdentifier = unit.chassis_number?.trim();
        const hasCondition = unit.condition;

        return hasIdentifier && hasCondition;
      });
      
      return allValid;
    }
    
    return true;
  }, [showUnits, unitsExpanded, qty, unitsData]);

  const isSubmitDisabled = loading || !areUnitsValid;
  
  // Fetch products when dialog opens
  useEffect(() => {
    if (open) {
      reset({ movement_type: 'in', quantity: 1 });
      replace([]);
      setUnitsExpanded(false);
      productsAPI.getAll({ limit: 500 }).then(r => setProducts(r.data.data || [])).catch(() => {});
    }
  }, [open, reset, replace]);

  // Sync unit rows count with quantity (only when accordion is expanded)
  useEffect(() => {
    if (!unitsExpanded || !showUnits) return;
    const diff = qty - fields.length;
    if (diff > 0) for (let i = 0; i < diff; i++) append(emptyUnit());
    else if (diff < 0) for (let i = 0; i < -diff; i++) remove(fields.length - 1 - i);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qty, unitsExpanded]);

  // When accordion is opened, ensure row count matches qty
  const handleAccordionChange = (_, expanded) => {
    setUnitsExpanded(expanded);
    if (expanded && fields.length === 0) {
      for (let i = 0; i < qty; i++) append(emptyUnit());
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const payload = {
        product_id:    data.product_id,
        warehouse_id:  data.warehouse_id,
        quantity:      parseInt(data.quantity),
        movement_type: data.movement_type,
        stock_alert:  parseInt(data.stock_alert) || 0,
        notes:         data.notes || null,
      };

      // Only attach units if accordion was expanded and rows are present
      if (unitsExpanded && data.units?.length > 0) {
        payload.units = data.units.map(u => ({
          chassis_number:    u.chassis_number || undefined,
          condition:         u.condition      || 'new',
          color:             u.color          || undefined,
          year:              u.year           ? parseInt(u.year) : undefined,
          notes:             u.notes          || undefined,
          custom_attributes: u.custom_attributes || {},
        }));
      }

      await inventoryAPI.adjustStock(payload);
      window.dispatchEvent(new CustomEvent('inventory-updated'));
      toast.success(`Stock adjusted${payload.units ? ` — ${payload.units.length} unit(s) tracked` : ''}`);
      reset();
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to adjust stock');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tune color="primary" /> Adjust Stock
        </DialogTitle>

        <DialogContent dividers>
          {/* ── BULK FIELDS ── */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Controller name="product_id" control={control} rules={{ required: 'Please select a product' }}
                render={({ field }) => (
                  <Autocomplete
                    options={products}
                    getOptionLabel={o => `${o.brand_name ? o.brand_name + ' ' : ''}${o.name} ${o.model || ''} — ${o.sku}`}
                    onChange={(_, v) => field.onChange(v ? v.id : '')}
                    renderInput={params => (
                      <TextField {...params} label="Product / Vehicle *" size="small"
                        error={!!errors.product_id}
                        helperText={errors.product_id?.message || 'Search by name or SKU'} />
                    )}
                  />
                )} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Warehouse *</InputLabel>
                <Select {...register('warehouse_id', { required: 'Please select a warehouse' })}
                  label="Warehouse *" defaultValue="">
                  <MenuItem value="">— Select Warehouse —</MenuItem>
                  {warehouses.map(w => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Movement Type *</InputLabel>
                <Select {...register('movement_type', { required: true })}
                  label="Movement Type *" defaultValue="in">
                  <MenuItem value="in">Stock In</MenuItem>
                  <MenuItem value="out">Stock Out</MenuItem>
                  <MenuItem value="adjustment">Adjustment</MenuItem>
                  <MenuItem value="return">Return</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Quantity *" type="number" size="small"
                {...register('quantity', { required: true, min: { value: 1, message: 'Min 1' }, valueAsNumber: true })}
                error={!!errors.quantity} helperText={errors.quantity?.message}
                inputProps={{ min: 1 }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Stock Alert" type="number" size="small"
                {...register('stock_alert')}
                helperText="Set minimum stock level for alert" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notes (optional)" size="small" {...register('notes')} />
            </Grid>
          </Grid>

          {/* ── PER-UNIT DETAILS ACCORDION (only for stock-in / return) ── */}
          {showUnits && (
            <Accordion
              expanded={unitsExpanded}
              onChange={handleAccordionChange}
              variant="outlined"
              sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <QrCode2 color={unitsExpanded ? 'primary' : 'action'} fontSize="small" />
                  <Typography fontWeight={600}>
                    Unit-Level Details
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      (required for Stock In/Return — capture chassis and unit-specific details per item)
                    </Typography>
                  </Typography>
                  {unitsExpanded && (
                    <Chip label={`${qty} unit${qty !== 1 ? 's' : ''}`} size="small" color="primary" sx={{ ml: 1 }} />
                  )}
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ pt: 0 }}>
                {fields.map((field, idx) => (
                  <Paper key={field.id} variant="outlined"
                    sx={{ p: 2, mb: 1.5, borderRadius: 2, position: 'relative' }}>
                    <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 1.5 }}>
                      Unit #{idx + 1}
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={4}>
                        <TextField fullWidth size="small" label="Chassis / VIN"
                          {...register(`units.${idx}.chassis_number`)} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Condition</InputLabel>
                          <Select {...register(`units.${idx}.condition`)} label="Condition" defaultValue="new">
                            <MenuItem value="new">New</MenuItem>
                            <MenuItem value="used">Used</MenuItem>
                            <MenuItem value="certified_used">Certified Used</MenuItem>
                            <MenuItem value="damaged">Damaged</MenuItem>
                            <MenuItem value="refurbished">Refurbished</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Controller
                          name={`units.${idx}.color`}
                          control={control}
                          render={({ field }) => (
                            <Autocomplete
                              freeSolo
                              options={COLOR_OPTIONS}
                              value={field.value || ''}
                              onChange={(_, value) => field.onChange(value || '')}
                              onInputChange={(_, value, reason) => {
                                if (reason === 'input') field.onChange(value);
                              }}
                              renderInput={params => (
                                <TextField {...params} fullWidth size="small" label="Color" />
                              )}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={4} sm={2}>
                        <Controller
                          name={`units.${idx}.year`}
                          control={control}
                          render={({ field }) => (
                            <Autocomplete
                              freeSolo
                              options={YEAR_OPTIONS}
                              value={field.value || ''}
                              onChange={(_, value) => field.onChange(value || '')}
                              onInputChange={(_, value, reason) => {
                                if (reason === 'input') field.onChange(value);
                              }}
                              renderInput={params => (
                                <TextField {...params} fullWidth size="small" label="Year" />
                              )}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField fullWidth size="small" label="Notes"
                          {...register(`units.${idx}.notes`)} />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </AccordionDetails>
            </Accordion>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={isSubmitDisabled} 
            startIcon={loading ? <CircularProgress size={18} /> : <Tune />}
          >
            {loading ? 'Saving…' : 'Adjust Stock'}
          </Button>
          {!areUnitsValid && (
            <Typography variant="caption" color="error" sx={{ ml: 2, alignSelf: 'center' }}>
              {showUnits && !unitsExpanded 
                ? 'Unit details are required for Stock In/Return movements. Please expand and fill unit details.'
                : 'Please fill chassis number and condition for all units'
              }
            </Typography>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

// ══════════════════════════════════════════════════════════════
// MAIN INVENTORY PAGE
// ══════════════════════════════════════════════════════════════
const InventoryPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [tab, setTab] = useState(0);
  const [productModels, setProductModels] = useState({}); // Store models by product_id

  // Function to fetch product model
  const fetchProductModel = async (productId) => {
    if (productModels[productId]) {
      return productModels[productId];
    }
    
    try {
      const response = await productsAPI.getById(productId);
      const model = response.data.data.model;
      setProductModels(prev => ({ ...prev, [productId]: model }));
      return model;
    } catch {
      return null;
    }
  };

  const fetchInventory = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await inventoryAPI.getAll({
        page: page + 1, limit: rowsPerPage,
        search: search || undefined,
        warehouse_id: selectedWarehouse || undefined,
        low_stock: tab === 1 ? 'true' : undefined,
      });
      setItems(data.data || []);
      setTotal(data.pagination?.total || 0);
    } catch {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, selectedWarehouse, tab]);

  useEffect(() => {
    fetchInventory();
    inventoryAPI.getWarehouses().then(r => setWarehouses(r.data.data || [])).catch(() => {});
    
    // Listen for inventory updates from other components
    const handleInventoryUpdate = () => {
      fetchInventory();
    };
    
    window.addEventListener('inventory-updated', handleInventoryUpdate);
    
    return () => {
      window.removeEventListener('inventory-updated', handleInventoryUpdate);
    };
  }, [fetchInventory]);

  const aggregateColumns = [
    { key: 'sku', label: 'SKU',
      render: v => <Typography fontFamily="monospace" fontSize="0.8rem" fontWeight={600}>{v}</Typography> },
    { key: 'product_name', label: 'Product',
      render: (v, row) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{v}</Typography>
          <Typography variant="caption" color="text.secondary">{row.brand_name}</Typography>
        </Box>
      )},
    { key: 'model', label: 'Model',
      render: (v, row) => {
        // Try to get model from cached state or fetch it
        const model = productModels[row.product_id] || v;
        if (!model && row.product_id) {
          // Trigger async fetch (fire and forget for UI responsiveness)
          fetchProductModel(row.product_id);
        }
        
        return model
          ? <Typography fontFamily="monospace" fontSize="0.8rem" fontWeight={600}>{model}</Typography>
          : <Typography color="text.secondary" fontSize="0.8rem" fontStyle="italic">
              {row.product_name?.split(' ').slice(0, 2).join(' ') || 'Loading...'}
            </Typography>;
      } },
    { key: 'warehouse_name', label: 'Warehouse' },
    { key: 'quantity', label: 'In Stock', align: 'right',
      render: (v, row) => (
        <Chip 
          label={v} 
          size="small"
          color={v <= row.min_quantity ? 'error' : v <= row.min_quantity * 2 ? 'warning' : 'success'}
          onClick={() => {
            const searchParams = new URLSearchParams();
            const selectedModel = productModels[row.product_id] || row.model;
            searchParams.set('search', row.product_name || '');

            if (selectedModel) {
              searchParams.set('model', selectedModel);
            }

            if (row.warehouse_id) {
              searchParams.set('warehouse_id', row.warehouse_id);
            }
            navigate(`/unit-tracker?${searchParams.toString()}`);
          }}
          sx={{ 
            cursor: 'pointer',
            '&:hover': { 
              transform: 'scale(1.05)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        />
      )},
    { key: 'available_quantity', label: 'Available', align: 'right' },
    { key: 'reserved_quantity', label: 'Reserved', align: 'right' },
    { key: 'min_quantity', label: 'Min Qty', align: 'right' },
    { key: 'product_status', label: 'Status',
      render: v => {
        const colorMap = {
          'available': 'success',
          'low_stock': 'warning',
          'out_of_stock': 'error',
        };
        return (
          <Chip label={v?.replace('_', ' ') || 'N/A'} size="small"
            color={colorMap[v] || 'default'} />
        );
      }},
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Inventory</Typography>
          <Typography variant="body2" color="text.secondary">
            {total} stock line{total !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Tune />} onClick={() => setAdjustOpen(true)}>
          Adjust Stock
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => { setTab(v); setPage(0); }} sx={{ mb: 2 }}>
        <Tab label="All Stock" />
        <Tab label="Low Stock Alerts" />
      </Tabs>

      {/* Filters for aggregate tabs */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          placeholder="Search by product name or SKU…" size="small" sx={{ minWidth: 320 }}
          value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Warehouse</InputLabel>
          <Select value={selectedWarehouse} label="Warehouse"
            onChange={e => { setSelectedWarehouse(e.target.value); setPage(0); }}>
            <MenuItem value="">All Warehouses</MenuItem>
            {warehouses.map(w => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <DataTable
        columns={aggregateColumns} rows={items} loading={loading} total={total}
        page={page} rowsPerPage={rowsPerPage}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
        emptyMessage={tab === 1 ? 'No low stock items' : 'No inventory records found'}
      />

      <AdjustDialog
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        onSaved={fetchInventory}
        warehouses={warehouses}
      />
    </Box>
  );
};

export default InventoryPage;
